import React, { useState, useRef, useCallback } from 'react';
import { Button, Card, Badge, Spinner } from 'react-bootstrap';
import { useApolloClient, useQuery } from '@apollo/client';
import { GET_CONTRACT_TEMPLATE } from '../../api/graphql/queries/getContractTemplateQuery';
import {
  documentUploadHelpers,
  DocumentUploadResponse
} from '../../api/graphql/mutations/documentUpload';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faUpload, 
  faExternalLinkAlt, 
  faIdCard, 
  faFileText, 
  faBuilding, 
  faAddressCard, 
  faMoneyCheckAlt, 
  faClipboardCheck 
} from '@fortawesome/free-solid-svg-icons';

// Document type constants
const DOCUMENT_TYPES = {
  CONTRACT: 'contract',
  PATENTE: 'patente',
  RNE: 'rne',
  CIN_RECTO: 'cinRecto',
  CIN_VERSO: 'cinVerso', 
  RIB: 'rib'
};

// Status text constants
const STATUS = {
  NOT_UPLOADED: 'Not Uploaded',
  PENDING: 'Pending',
  APPROVED: 'Approved',
  REJECTED: 'Rejected',
  UPLOAD_SUCCESS: 'Upload Successful',
  UPLOAD_ERROR: 'Error',
  STATUS_UNAVAILABLE: 'Status Unavailable'
};

// Validation status code constants
const VALIDATION_STATUS = {
  // Contract validation
  CONTRACT_PENDING: '18456',
  CONTRACT_APPROVED: '18457',
  CONTRACT_REJECTED: '18458',

  // Patente validation
  PATENTE_PENDING: '18459',
  PATENTE_APPROVED: '18460',
  PATENTE_REJECTED: '18461',

  // RNE validation
  RNE_PENDING: '18468',
  RNE_APPROVED: '18469',
  RNE_REJECTED: '18470',

  // CIN Recto validation
  CIN_RECTO_PENDING: '18462',
  CIN_RECTO_APPROVED: '18463',
  CIN_RECTO_REJECTED: '18464',

  // CIN Verso validation
  CIN_VERSO_PENDING: '18465',
  CIN_VERSO_APPROVED: '18466',
  CIN_VERSO_REJECTED: '18467',

  // RIB validation
  RIB_PENDING: '18471',
  RIB_APPROVED: '18472',
  RIB_REJECTED: '18473'
};

// Component props interface
interface DocumentUploadProps {
  defaultDocUrls: Record<string, string>;
  validationStatuses: Record<string, string>;
  onDocumentsUploaded?: (documents: Record<string, string>) => void;
}

// Document field interface
interface DocumentField {
  id: string;
  label: string;
  uploadFn: (client: any, file: File) => Promise<DocumentUploadResponse>;
  description: string;
  required?: boolean;
}

/**
 * Maps a validation status code to a display state
 */
const getStatusInfo = (statusCode: string | undefined): { text: string, variant: string } => {
  if (!statusCode) {
    return { text: STATUS.STATUS_UNAVAILABLE, variant: 'secondary' };
  }
  
  // Map of all status codes to their display info
  const statusMap: Record<string, { text: string, variant: string }> = {
    // Contract validation
    [VALIDATION_STATUS.CONTRACT_PENDING]: { text: STATUS.PENDING, variant: 'warning' },
    [VALIDATION_STATUS.CONTRACT_APPROVED]: { text: STATUS.APPROVED, variant: 'success' },
    [VALIDATION_STATUS.CONTRACT_REJECTED]: { text: STATUS.REJECTED, variant: 'danger' },
    
    // Patente validation
    [VALIDATION_STATUS.PATENTE_PENDING]: { text: STATUS.PENDING, variant: 'warning' },
    [VALIDATION_STATUS.PATENTE_APPROVED]: { text: STATUS.APPROVED, variant: 'success' },
    [VALIDATION_STATUS.PATENTE_REJECTED]: { text: STATUS.REJECTED, variant: 'danger' },
    
    // RNE validation
    [VALIDATION_STATUS.RNE_PENDING]: { text: STATUS.PENDING, variant: 'warning' },
    [VALIDATION_STATUS.RNE_APPROVED]: { text: STATUS.APPROVED, variant: 'success' },
    [VALIDATION_STATUS.RNE_REJECTED]: { text: STATUS.REJECTED, variant: 'danger' },
    
    // CIN Recto validation
    [VALIDATION_STATUS.CIN_RECTO_PENDING]: { text: STATUS.PENDING, variant: 'warning' },
    [VALIDATION_STATUS.CIN_RECTO_APPROVED]: { text: STATUS.APPROVED, variant: 'success' },
    [VALIDATION_STATUS.CIN_RECTO_REJECTED]: { text: STATUS.REJECTED, variant: 'danger' },
    
    // CIN Verso validation
    [VALIDATION_STATUS.CIN_VERSO_PENDING]: { text: STATUS.PENDING, variant: 'warning' },
    [VALIDATION_STATUS.CIN_VERSO_APPROVED]: { text: STATUS.APPROVED, variant: 'success' },
    [VALIDATION_STATUS.CIN_VERSO_REJECTED]: { text: STATUS.REJECTED, variant: 'danger' },
    
    // RIB validation
    [VALIDATION_STATUS.RIB_PENDING]: { text: STATUS.PENDING, variant: 'warning' },
    [VALIDATION_STATUS.RIB_APPROVED]: { text: STATUS.APPROVED, variant: 'success' },
    [VALIDATION_STATUS.RIB_REJECTED]: { text: STATUS.REJECTED, variant: 'danger' }
  };
  
  return statusMap[statusCode] || { text: STATUS.PENDING, variant: 'warning' };
};

const DocumentUploadSection: React.FC<DocumentUploadProps> = ({
  defaultDocUrls,
  validationStatuses,
  onDocumentsUploaded
}) => {
  // State for uploaded documents - initialized directly with defaultDocUrls prop
  const [uploadedDocs, setUploadedDocs] = useState<Record<string, string>>(defaultDocUrls || {});
  
  // Track upload status (loading, errors)
  const [uploadStatus, setUploadStatus] = useState<Record<string, { loading: boolean; error?: string; success?: boolean }>>({});
  
  const client = useApolloClient();
  const { t } = useTranslation();
  const fileInputRefs = useRef<Record<string, HTMLInputElement | null>>({});

  // Document field definitions
  const documentFields: DocumentField[] = [
    {
      id: DOCUMENT_TYPES.CONTRACT,
      label: 'CONTRACT',
      uploadFn: documentUploadHelpers.uploadContract,
      description: 'Téléchargez votre contrat vendeur signé avec Wamia.',
      required: true
    },
    {
      id: DOCUMENT_TYPES.PATENTE,
      label: 'PATENTE',
      uploadFn: documentUploadHelpers.uploadPatente,
      description: 'Téléchargez votre licence commerciale ou document de patente.',
      required: true
    },
    {
      id: DOCUMENT_TYPES.RNE,
      label: 'RNE',
      uploadFn: documentUploadHelpers.uploadRne,
      description: 'Téléchargez votre certificat du Registre National des Entreprises (RNE).',
      required: true
    },
    {
      id: DOCUMENT_TYPES.CIN_RECTO,
      label: 'CIN RECTO',
      uploadFn: documentUploadHelpers.uploadCinRecto,
      description: 'Téléchargez le recto de votre carte d\'identité nationale.',
      required: true
    },
    {
      id: DOCUMENT_TYPES.CIN_VERSO,
      label: 'CIN VERSO',
      uploadFn: documentUploadHelpers.uploadCinVerso,
      description: 'Téléchargez le verso de votre carte d\'identité nationale.',
      required: true
    },
    {
      id: DOCUMENT_TYPES.RIB,
      label: 'RIB',
      uploadFn: documentUploadHelpers.uploadRib,
      description: 'Téléchargez votre document de Relevé d\'Identité Bancaire (RIB).',
      required: true
    }
  ];

  /**
   * Handle file selection and upload
   */
  const handleFileChange = useCallback(async (event: React.ChangeEvent<HTMLInputElement>, field: DocumentField) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type (only PDF)
    if (file.type !== 'application/pdf') {
      setUploadStatus(prev => ({
        ...prev,
        [field.id]: { loading: false, error: "Seuls les fichiers PDF sont autorisés" }
      }));
      return;
    }

    // Start loading state
    setUploadStatus(prev => ({
      ...prev,
      [field.id]: { loading: true }
    }));

    try {
      // Upload the file
      const response = await field.uploadFn(client, file);

      if (response.success && response.url) {
        // Update the uploaded docs state with the new URL
        const updatedDocs = {
          ...uploadedDocs,
          [field.id]: response.url as string
        };
        
        setUploadedDocs(updatedDocs);
        
        // Set success status
        setUploadStatus(prev => ({
          ...prev,
          [field.id]: { loading: false, success: true }
        }));

        // Call the parent callback with updated documents
        if (onDocumentsUploaded) {
          onDocumentsUploaded(updatedDocs);
        }
      } else {
        // Set error status
        setUploadStatus(prev => ({
          ...prev,
          [field.id]: { loading: false, error: response.message || "Une erreur s'est produite lors du téléchargement" }
        }));
      }
    } catch (error) {
      console.error(`Error uploading ${field.id}:`, error);
      setUploadStatus(prev => ({
        ...prev,
        [field.id]: { loading: false, error: "Une erreur s'est produite lors du téléchargement" }
      }));
    }
    
    // Reset the file input
    if (fileInputRefs.current[field.id]) {
      fileInputRefs.current[field.id]!.value = '';
    }
  }, [uploadedDocs, onDocumentsUploaded, client]);

  /**
   * Get the appropriate icon for each document type
   */
  const getDocumentIcon = (docId: string) => {
    switch(docId) {
      case DOCUMENT_TYPES.CONTRACT: return faFileText;
      case DOCUMENT_TYPES.PATENTE: return faBuilding;
      case DOCUMENT_TYPES.RNE: return faClipboardCheck;
      case DOCUMENT_TYPES.CIN_RECTO: 
      case DOCUMENT_TYPES.CIN_VERSO: return faIdCard;
      case DOCUMENT_TYPES.RIB: return faMoneyCheckAlt;
      default: return faFileText;
    }
  };

  /**
   * Get status for a document
   */
  const getDocumentStatus = (docId: string) => {
    // If document is not uploaded, return "Not Uploaded" status
    if (!uploadedDocs[docId]) {
      return { text: STATUS.NOT_UPLOADED, variant: 'secondary' };
    }
    
    // Check for error status from upload attempt
    if (uploadStatus[docId]?.error) {
      return { text: STATUS.UPLOAD_ERROR, variant: 'danger' };
    }
    
    // Check for successful upload but no validation status yet
    if (uploadStatus[docId]?.success && !validationStatuses[docId]) {
      return { text: STATUS.UPLOAD_SUCCESS, variant: 'info' };
    }
    
    // Get status based on validation code
    return getStatusInfo(validationStatuses[docId]);
  };

  // Query for contract template
  const { loading: templateLoading, error: templateError, data: templateData } = useQuery(GET_CONTRACT_TEMPLATE, {
    fetchPolicy: 'network-only'
  });

  // Get template URL from the query response
  const templateUrl = templateData?.getContractTemplate?.template_url || '';

  return (
    <div className="document-upload-section">
      {/* Card 1: Contract Template - Only for downloading the template */}
      <Card className="mb-4 shadow-sm">
        <Card.Body>
          {/* Header */}
          <div className="mb-3">
            <div className="d-flex align-items-center mb-2">
              <FontAwesomeIcon icon={faFileText} className="me-2 text-warning" size="lg" />
              <h5 className="mb-0 fw-bold">Contract Template</h5>
            </div>
            <p className="text-muted small">Download our seller agreement contract, sign it, and upload it back.</p>
          </div>
          
          {/* Download button only */}
          <div className="d-flex gap-2">
            {templateLoading ? (
              <Button variant="outline-warning" className="d-flex align-items-center" disabled>
                <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-2" />
                Loading Template...
              </Button>
            ) : templateError ? (
              <Button variant="outline-danger" className="d-flex align-items-center" disabled>
                <FontAwesomeIcon icon={faFileText} className="me-2" />
                Error Loading Template
              </Button>
            ) : templateUrl ? (
              <Button 
                variant="outline-warning" 
                className="d-flex align-items-center"
                as="a"
                href={templateUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                <FontAwesomeIcon icon={faFileText} className="me-2" />
                Download Template
              </Button>
            ) : (
              <Button variant="outline-warning" className="d-flex align-items-center" disabled>
                <FontAwesomeIcon icon={faFileText} className="me-2" />
                No Template Available
              </Button>
            )}
          </div>
        </Card.Body>
      </Card>
      
      {/* Card 2: Required Documents - Including contract upload */}
      <Card className="mb-4 shadow-sm">
        <Card.Body>
          {/* Header */}
          <div className="mb-3">
            <div className="d-flex align-items-center mb-2">
              <FontAwesomeIcon icon={faUpload} className="me-2 text-warning" size="lg" />
              <h5 className="mb-0 fw-bold">Required Documents</h5>
            </div>
            <p className="text-muted small">Upload your verification documents to complete the KYC process.</p>
          </div>
          
          {/* Document List - Now includes contract */}
          <div className="document-list">
            {documentFields.map(field => {
              const status = getDocumentStatus(field.id);
              return (
                <div key={field.id} className="document-item d-flex justify-content-between align-items-center mb-3 p-2 border-bottom">
                  <div className="d-flex align-items-center">
                    <div className="document-icon p-2 rounded me-3" 
                      style={{ 
                        backgroundColor: `rgba(var(--bs-${status.variant}-rgb), 0.15)` 
                      }}>
                      <FontAwesomeIcon icon={getDocumentIcon(field.id)} className={`text-${status.variant}`} />
                    </div>
                    <div>
                      <p className="mb-0 fw-bold">{field.label}</p>
                      <Badge 
                        bg={`light-${status.variant}`} 
                        text={status.variant}
                        style={{ 
                          backgroundColor: `rgba(var(--bs-${status.variant}-rgb), 0.15)`,
                          color: `var(--bs-${status.variant})`
                        }}
                      >
                        {status.text}
                      </Badge>
                    </div>
                  </div>
                  <div>
                    <input
                      type="file"
                      accept="application/pdf"
                      id={`file-${field.id}`}
                      onChange={(e) => handleFileChange(e, field)}
                      className="d-none"
                      ref={el => fileInputRefs.current[field.id] = el}
                    />
                    {uploadedDocs[field.id] ? (
                      <div className="d-flex gap-2">
                        {/* Replace button - only show if status is not APPROVED */}
                        {status.text !== STATUS.APPROVED && (
                          <Button 
                            variant="outline-warning" 
                            size="sm"
                            onClick={() => fileInputRefs.current[field.id]?.click()}
                            disabled={uploadStatus[field.id]?.loading}
                          >
                            <FontAwesomeIcon icon={faUpload} className="me-1" />
                            Replace
                          </Button>
                        )}
                        
                        {/* View button - always show */}
                        <Button 
                          variant="warning" 
                          size="sm"
                          as="a"
                          href={uploadedDocs[field.id]}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <FontAwesomeIcon icon={faExternalLinkAlt} className="me-1" />
                          View
                        </Button>
                      </div>
                    ) : (
                      <Button 
                        variant="warning" 
                        size="sm"
                        onClick={() => fileInputRefs.current[field.id]?.click()}
                        disabled={uploadStatus[field.id]?.loading}
                      >
                        <FontAwesomeIcon icon={faUpload} className="me-1" />
                        Upload File
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </Card.Body>
      </Card>
      
      {/* Style definitions */}
      <style>
        {`
          .bg-light-success {
            background-color: rgba(25, 135, 84, 0.15) !important;
          }
          .bg-light-warning {
            background-color: rgba(255, 193, 7, 0.15) !important;
          }
          .bg-light-danger {
            background-color: rgba(220, 53, 69, 0.15) !important;
          }
          .bg-light-secondary {
            background-color: rgba(108, 117, 125, 0.15) !important;
          }
          .bg-light-info {
            background-color: rgba(13, 202, 240, 0.15) !important;
          }
          
          .document-icon {
            display: flex;
            align-items: center;
            justify-content: center;
            width: 40px;
            height: 40px;
            border-radius: 8px;
          }
        `}
      </style>
    </div>
  );
};

export default DocumentUploadSection;
